import { useSyncExternalStore } from 'react';
import { consolePathForRole, type AdminRole } from '@/lib/admin/roles';
import { canAccessLegacyOperations } from '@/lib/admin/permissions';

function subscribePathname(onChange: () => void) {
  window.addEventListener('popstate', onChange);
  const push = history.pushState.bind(history);
  const replace = history.replaceState.bind(history);
  history.pushState = (...args) => {
    push(...args);
    onChange();
  };
  history.replaceState = (...args) => {
    replace(...args);
    onChange();
  };
  return () => {
    window.removeEventListener('popstate', onChange);
    history.pushState = push;
    history.replaceState = replace;
  };
}

function getPathname() {
  return window.location.pathname;
}

function usePathname() {
  return useSyncExternalStore(subscribePathname, getPathname, () => '/');
}

interface NavItem {
  href: string;
  label: string;
  icon: string;
}

const MEMBER_NAV: NavItem[] = [
  { href: '/', label: '首页工作台', icon: 'ri-home-5-line' },
  { href: '/board', label: '角色作战台', icon: 'ri-dashboard-3-line' },
  { href: '/materials', label: '角色素材库', icon: 'ri-folder-image-line' },
  { href: '/role-video', label: '角色视频', icon: 'ri-movie-2-line' },
  { href: '/studio', label: '生成中心', icon: 'ri-magic-line' },
  { href: '/reply', label: '回复优化', icon: 'ri-chat-smile-2-line' },
  { href: '/queue', label: '生成队列', icon: 'ri-list-check-2' },
  { href: '/me', label: '我的纪录', icon: 'ri-history-line' },
];

function adminNavForRole(role: AdminRole): NavItem[] {
  switch (role) {
    case 'admin':
      return [{ href: '/admin/support', label: '会员支持后台', icon: 'ri-customer-service-2-line' }];
    case 'super_admin':
      return [
        { href: '/admin/business', label: '商业营运后台', icon: 'ri-bar-chart-box-line' },
        ...(canAccessLegacyOperations(role)
          ? [{ href: '/admin/operations', label: '完整营运后台', icon: 'ri-shield-star-line' }]
          : []),
      ];
    case 'dev_admin':
      return [
        { href: consolePathForRole(role), label: '平台稳定控制台', icon: 'ri-pulse-line' },
        ...(canAccessLegacyOperations(role)
          ? [{ href: '/admin/operations', label: '完整营运后台', icon: 'ri-shield-star-line' }]
          : []),
      ];
    default:
      return [];
  }
}

function isActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function NavLink({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  onNavigate?: () => void;
}) {
  const active = isActive(pathname, item.href);
  return (
    <a
      href={item.href}
      onClick={onNavigate}
      className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-primary-500 text-background-50'
          : 'text-foreground-600 hover:bg-secondary-100'
      }`}
    >
      <i className={`${item.icon} text-base`} />
      <span>{item.label}</span>
    </a>
  );
}

export interface AstraeaSidebarProps {
  adminRole: AdminRole;
  onNavigate?: () => void;
}

export default function AstraeaSidebar({ adminRole, onNavigate }: AstraeaSidebarProps) {
  const pathname = usePathname();
  const adminNav = adminNavForRole(adminRole);

  return (
    <>
      <div className="px-1 pb-4 border-b border-secondary-100">
        <a href="/" onClick={onNavigate} className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center shrink-0">
            <i className="ri-sparkling-2-line text-background-50 text-lg" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground-950 leading-tight">Astraea</div>
            <div className="text-[10px] text-secondary-400 leading-tight">AI 角色经营平台</div>
          </div>
        </a>
      </div>

      <nav className="flex-1 overflow-y-auto px-1 py-4 space-y-1">
        {MEMBER_NAV.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} onNavigate={onNavigate} />
        ))}

        {adminNav.length > 0 && (
          <div className="pt-4 mt-2 border-t border-secondary-100">
            <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-secondary-400">
              管理后台
            </div>
            <div className="space-y-1">
              {adminNav.map((item) => (
                <NavLink key={item.href} item={item} pathname={pathname} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
